import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { accountHistoryUrl, hierarchyUrl } from "./urls";
import { loadTranscript, type DocumentReference, type TranscriptPage, type TranscriptSelection } from "./transcript";
import { DocumentView } from "./DocumentView";
import type { DocumentSelection } from "./document";
import { ThreadComposer } from "./ThreadComposer";
import { CreateConversation } from "./CreateConversation";
import { forkEligibility } from "./fork";
import { hasLocalForkRecovery } from "./create";
import { HistoricalMessages } from "./HistoricalMessages";

export function TranscriptView({ selection }: { selection: TranscriptSelection }) {
  const { accountId, workspaceId, projectId, threadId, accountHistory } = selection;
  const key = JSON.stringify([accountId, workspaceId, projectId, threadId, accountHistory]);
  const pending = useRef<AbortController | undefined>(undefined);
  const [state, setState] = useState<{ key: string; page?: TranscriptPage; failed?: boolean; refreshing?: boolean }>();
  const [document, setDocument] = useState<{ key: string; selection: DocumentSelection }>();
  const [branch, setBranch] = useState<{ key: string; nodeId: string }>();

  function read(previous?: TranscriptPage, preserveEditor = false): void {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    // Native file pickers return window focus before dispatching their file
    // change. Preserve the mounted input while concealing stale content;
    // every upload still reauthorizes, and a failed refresh removes it.
    setState(value => preserveEditor && value?.key === key && value.page
      ? { ...value, refreshing: true }
      : undefined);
    void loadTranscript({ accountId, workspaceId, projectId, threadId, accountHistory }, previous, controller.signal).then(
      (page) => { if (!controller.signal.aborted) setState({ key, page }); },
      () => { if (!controller.signal.aborted) setState({ key, failed: true }); },
    );
  }

  useEffect(() => {
    read();
    const refresh = () => read(undefined, true);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      pending.current?.abort();
    };
  }, [accountId, workspaceId, projectId, threadId, accountHistory]);

  const current = state?.key === key ? state : undefined;
  const back = accountHistory ? accountHistoryUrl(accountId) : hierarchyUrl(accountId, workspaceId, projectId);
  if (!current) return <div role="status" className="p-8 text-sm text-[#52606e]">Loading conversation…</div>;
  if (current.failed || !current.page) return <main className="mx-auto max-w-xl px-6 py-12">
    <h1 className="text-xl font-semibold">Conversation unavailable</h1>
    <p className="mt-3 text-sm leading-6 text-[#52606e]">Your access may have changed. Check your account or try again when you're connected.</p>
    <Button className="mt-5" onClick={() => read()}>Try again</Button>
    <a className="ml-5 text-sm underline" href={back}>Back to conversations</a>
  </main>;
  const { page } = current;
  const canWrite = page.canWrite === true && !accountHistory;
  if (document?.key === key) return <DocumentView selection={document.selection} onClose={() => { setDocument(undefined); read(); }} />;
  function openDocument(nodeId: string, reference: DocumentReference) {
    setDocument({ key, selection: { accountId, workspaceId, projectId, threadId, nodeId, reference } });
  }
  return <>
    {current.refreshing ? <div role="status" className="p-8 text-sm text-[#52606e]">Loading conversation…</div> : null}
    <main hidden={current.refreshing} inert={current.refreshing} className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-12 text-[#20272b]">
    <a className="text-sm text-[#52606e] underline" href={back}>Back to conversations</a>
    <div className="mt-5 flex items-start justify-between gap-4">
      <h1 className="min-w-0 break-words text-2xl font-semibold tracking-[-0.04em]">{page.thread.name}</h1>
      <Button variant="ghost" onClick={() => read()}>Refresh</Button>
    </div>
    {page.nextNode ? <Button className="mt-6" variant="ghost" onClick={() => read(page)}>Load earlier messages</Button> : null}
    {page.turns.length ? <ol className="mt-8 space-y-10" aria-label="Conversation history">
      {page.turns.map((turn) => <li key={turn.id} className="space-y-4">
        {turn.legacy ? <HistoricalMessages messages={turn.legacy.messages} /> : <>
        <div className="ml-auto max-w-[90%] rounded-2xl bg-[#dceffc] px-4 py-3">
          <p className="whitespace-pre-wrap break-words text-sm leading-6">{turn.input}</p>
          <Documents label="Attached document" references={turn.inputs} onOpen={(ref) => openDocument(turn.id, ref)} />
        </div>
        <div className="max-w-full px-1">
          <p className="whitespace-pre-wrap break-words text-sm leading-6">{turn.reply}</p>
          <Documents label="Created document" references={turn.outputs} onOpen={(ref) => openDocument(turn.id, ref)} />
          {turn.outcome === "failed" ? <p className="mt-2 text-sm text-[#52606e]">This request couldn't be completed.</p> : null}
          {turn.outcome === "cancelled" ? <p className="mt-2 text-sm text-[#52606e]">This request was stopped.</p> : null}
          {turn.outcome === "needs_more_credits" ? <p className="mt-2 text-sm text-[#52606e]">More credits are needed to continue.</p> : null}
        </div>
        </>}
        {canWrite ? <ForkControl key={`${key}:${turn.id}`} selection={selection} nodeId={turn.id} userId={page.catalog.scope.userId} revision={page} open={branch?.key === key && branch.nodeId === turn.id} onOpen={() => setBranch({key,nodeId:turn.id})} /> : null}
      </li>)}
    </ol> : <p className="mt-8 text-sm text-[#52606e]">No replies yet.</p>}
    {canWrite
      ? <ThreadComposer key={`${key}:${page.catalog.scope.userId}`} selection={selection} inline={branch?.key === key} onPage={(page) => setState(value => value?.refreshing ? value : { key, page })} />
      : <p role="status" className="mt-8 text-sm text-[#52606e]">You have read-only access to this conversation.</p>}
  </main></>;
}

function ForkControl({ selection, nodeId, userId, revision, open, onOpen }: { selection: TranscriptSelection; nodeId: string; userId: string; revision: TranscriptPage; open: boolean; onOpen: () => void }) {
  const key = JSON.stringify([selection.accountId, selection.workspaceId, selection.projectId, selection.threadId, nodeId, userId]);
  const [state, setState] = useState<{ key: string; available: boolean; recovery: boolean }>();
  const creation = {accountId:selection.accountId,workspaceId:selection.workspaceId,projectId:selection.projectId,sourceThreadId:selection.threadId,nodeId};
  useEffect(() => {
    const controller = new AbortController();
    setState(undefined);
    void Promise.all([forkEligibility(selection, nodeId, userId, controller.signal), hasLocalForkRecovery(creation, userId)]).then(
      ([available, recovery]) => { if (!controller.signal.aborted) setState({ key, available, recovery }); },
      () => { if (!controller.signal.aborted) setState({ key, available: false, recovery: false }); },
    );
    return () => controller.abort();
  }, [key, revision]);
  return <>
    {state?.key === key && (state.available || state.recovery) ? <Button variant="ghost" className="mt-3" onClick={onOpen}>{state.recovery ? "Resume separate conversation" : "Continue separately"}</Button> : null}
    {open ? <CreateConversation selection={creation} /> : null}
  </>;
}

function Documents({ label, references, onOpen }: { label: string; references: DocumentReference[]; onOpen: (ref: DocumentReference) => void }) {
  return references.length ? <ul className="mt-3 space-y-2 text-sm text-[#52606e]">{references.map((ref, index) => <li key={`${ref.artifactId}:${ref.versionId}`}><button className="underline" onClick={() => onOpen(ref)}>{label} {index + 1}</button></li>)}</ul> : null;
}
